import { Box, Container, Stack } from "@mui/material";
import "./styles.scss";
import { DashboardTopbar } from "./topbar";
import { DashboardSidebar } from "./sidebar/SidebarWrapper";
import { AdminSidebar } from "./sidebar/AdminSidebarWrapper";
import { Outlet, useNavigate, useLocation } from "react-router";
import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import DashboardFooterContext from "../../../contexts/DashboardFooterContext";
import { useUserContext } from "../../../hooks/userContext";
import { isAdminUser, Pages } from "../../../utils";
import { useEffectiveRole } from "../../../hooks/useEffectiveRole";

export const DashboardLayout = () => {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveRole, effectiveTier, canAccessAdminFeatures, isImpersonating } = useEffectiveRole();
  
  // Show admin sidebar only if effective role is admin AND user can access admin features
  // This means: show admin sidebar for actual admins, but not when impersonating
  const showAdminSidebar = effectiveRole === 'Administrator' && canAccessAdminFeatures;
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollTimer = useRef<NodeJS.Timeout | null>(null);


  // Redirect to appropriate dashboard when impersonation changes
  useEffect(() => {

    if (isImpersonating) {
      // When impersonating, redirect to Your Hub
      if (location.pathname === Pages.DASHBOARD || location.pathname.startsWith('/admin')) {
        navigate('/your-hub', { replace: true });
      }
    } else if (effectiveRole === 'Administrator') {
      // When stopping impersonation (returning to admin), redirect to dashboard
      if (location.pathname === '/your-hub') {
        navigate(Pages.DASHBOARD, { replace: true });
      }
    }
  }, [isImpersonating, effectiveRole, location.pathname, navigate]);

  const handleSidebarScroll = () => {
    if (scrollTimer.current) clearTimeout(scrollTimer.current);

    setShowScrollbar(true);

    requestAnimationFrame(() => {
      const el = document.querySelector('.sidebar-content');
      if (el) {
        el.dispatchEvent(new Event('scroll', { bubbles: true }));
      }
    });

    scrollTimer.current = setTimeout(() => {
      setShowScrollbar(false);
    }, 1200);
  };

  const [footerContent, setFooterContent] = useState<React.ReactNode | null>(null);

  const setFooter = useCallback((content: React.ReactNode) => {
    setFooterContent(content);
  }, []);

  const clearFooter = useCallback(() => {
    setFooterContent(null);
  }, []);

  const contextValue = useMemo(() => ({
    setFooter,
    clearFooter,
  }), [setFooter, clearFooter]);

  return (
    <div className="page dashboard-page" style={{ height: '100vh', position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box 
          className="sidebar"
          sx={{ 
            height: { xs: 'auto', sm: '100%' },
            width: { xs: '100%', sm: '240px' },
            minWidth: { xs: 'auto', sm: '240px' },
            maxWidth: { xs: '100%', sm: '240px' },
            flexShrink: 0,
            backgroundColor: '#374a67', // Ensure consistent sidebar background
          }}
        >
          {showAdminSidebar ? (
            <AdminSidebar
              showScrollbar={showScrollbar}
              handleSidebarScroll={handleSidebarScroll}
            />
          ) : (
            <DashboardSidebar
              showScrollbar={showScrollbar}
              handleSidebarScroll={handleSidebarScroll}
            />
          )}
        </Box>
        
        <Box 
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0, // This prevents flex item from overflowing
          }}
        >
          <DashboardTopbar />
          <DashboardFooterContext.Provider value={contextValue}>
            <Box 
              className="dashboard-content" 
              id="dashboard-content"
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0, // This allows the flex child to shrink and scroll
                padding: '24px', // Standardized padding for all sides including top
                backgroundColor: '#F5F5F5', // Ensure consistent background
                width: '100%', // Ensure full width
                maxWidth: '100%', // Prevent overflow
                // Custom scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#ccc',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#999',
                },
              }}
            >
              <Outlet key={`dashboard-${effectiveRole}-${effectiveTier}-${isImpersonating}`} />
            </Box>
            {footerContent && (
              <Box 
                className="dashboard-sticky-footer" 
                sx={{ 
                  position: 'sticky', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  zIndex: 10, 
                  backgroundColor: 'background.paper', 
                  borderTop: (theme) => `1px solid ${theme.palette.divider}` 
                }}
              >
                <Container maxWidth="xl" sx={{ py: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {footerContent}
                  </Stack>
                </Container>
              </Box>
            )}
          </DashboardFooterContext.Provider>
        </Box>

      </Box>
    </div>
  );
};